import { useEffect, useRef, useId } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptPromise = null;
function loadTurnstileScript() {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Cloudflare Turnstile CAPTCHA widget. Renders nothing (and never blocks the
 * form) until REACT_APP_TURNSTILE_SITE_KEY is set at build time — so
 * registration keeps working the same as before until a real site key is
 * configured. Calls onVerify(token) once solved and onExpire() if the
 * token expires before the form is submitted.
 */
export default function TurnstileWidget({ onVerify, onExpire }) {
  const containerId = useId().replace(/:/g, "");
  const widgetIdRef = useRef(null);

  useEffect(() => {
    if (!SITE_KEY) return;
    let cancelled = false;

    loadTurnstileScript().then(() => {
      if (cancelled || !window.turnstile) return;
      widgetIdRef.current = window.turnstile.render(`#${containerId}`, {
        sitekey: SITE_KEY,
        callback: (token) => onVerify?.(token),
        "expired-callback": () => onExpire?.(),
      });
    });

    return () => {
      cancelled = true;
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerId]);

  if (!SITE_KEY) return null;
  return <div id={containerId} style={{ margin: "8px 0" }} />;
}

export { SITE_KEY as TURNSTILE_SITE_KEY };
